// src/hooks/useInvoices.js
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { createAxiosInstance } from "../functions/axiosInstance";
import {
  cancelInvoice,
  createInvoice,
  createClientPaymentIntent,
  deleteInvoice,
  getClientInvoices,
  getDocumentShareUrl,
  getInvoices,
  getInvoicesForPortal,
  markInvoiceAsPaid,
  restoreInvoice,
  sendInvoiceEmail,
  updateInvoice,
} from "../functions/api_fethcers";

export const useInvoices = (
  clientId,
  {
    adminOptions = { page: 0, limit: 20, status: "all" },
    clientPortalOptions = { page: 0, limit: 20 },
    // Only poll while the caller is actually showing the billing tab, so
    // every admin client-detail page load doesn't keep hitting the invoices
    // endpoint every 30s regardless of the active tab.
    pollInterval = false,
  } = {}
) => {
  const queryClient = useQueryClient();
  const { page: adminPage, limit: adminLimit, status } = adminOptions;
  const { page: clientPortalPage, limit: clientPortalLimit } =
    clientPortalOptions;

  // -------------------------------------------
  // 1) Query: General/Admin Invoices
  // -------------------------------------------

  const { data, isLoading, isError, error, refetch } = useQuery(
    ["invoices", clientId, adminPage, adminLimit, status],
    () => getInvoices({ clientId, page: adminPage, limit: adminLimit, status }),
    {
      enabled: !!clientId,
      keepPreviousData: true,
      // Payment status changes out-of-band (client pays -> Stripe webhook -> DB),
      // so keep this fresh without a manual reload: refetch on remount/focus and
      // poll while the dashboard is open (only when the tab is focused).
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchInterval: pollInterval ? 30 * 1000 : false, // 30s live poll, billing tab only
    }
  );

  // -------------------------------------------
  // 2) New Query for Client Portal Invoices
  // -------------------------------------------
  //   - Only invoices that are public (have shareable links)
  //   - Statuses: 'pending', 'paid', 'overdue'
  //
  //   Using the new `getClientInvoices` fetcher
  // -------------------------------------------
  const {
    data: clientPortalData,
    isLoading: isLoadingClientPortal,
    isError: isErrorClientPortal,
    error: errorClientPortal,
    refetch: refetchClientPortal,
  } = useQuery(
    ["clientPortalInvoices", clientId, clientPortalPage, clientPortalLimit],
    () =>
      getClientInvoices({ page: clientPortalPage, limit: clientPortalLimit }),
    {
      enabled: false,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );

  // Parse out the client portal invoices
  const clientPortalInvoices = clientPortalData?.items || [];

  // Mutations
  const createInvoiceMutation = useMutation(createInvoice, {
    onSuccess: () => {
      queryClient.invalidateQueries(["invoices", clientId]);
      queryClient.invalidateQueries(["invoiceStats", clientId]);
      toast.success("Invoice created successfully");
    },
    onError: (error) =>
      toast.error(error.message || "Failed to create invoice"),
  });

  // Update invoice mutation
  const updateInvoiceMutation = useMutation(updateInvoice, {
    onMutate: async ({ id, ...updateData }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(["invoices", clientId]);

      // Snapshot the previous value
      const previousInvoices = queryClient.getQueryData([
        "invoices",
        clientId,
        adminPage,
        adminLimit,
        status,
      ]);

      // Get the existing invoice to check if it was published
      const existingInvoice = previousInvoices?.items?.find(
        (inv) => inv.id === id
      );
      const wasPublished =
        existingInvoice?.status === "pending" ||
        Boolean(
          existingInvoice?.document_id && existingInvoice?.shareable_link
        );

      // Optimistically update to the new value, ensuring unpublished state if needed
      queryClient.setQueryData(
        ["invoices", clientId, adminPage, adminLimit, status],
        (old) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.map((invoice) =>
              invoice.id === id
                ? {
                    ...invoice,
                    ...updateData,
                    // If it was published, ensure it's marked as draft and remove shareable link
                    ...(wasPublished
                      ? {
                          status: "draft",
                          shareable_link: null,
                        }
                      : {}),
                  }
                : invoice
            ),
          };
        }
      );

      return { previousInvoices, wasPublished };
    },
    onSuccess: (updatedInvoice, variables, context) => {
      toast.success("Invoice updated successfully");

      // If it was previously published, show additional info message
      if (context?.wasPublished) {
        toast.info(
          "Invoice has been unpublished. You'll need to publish it again to share it.",
          { autoClose: 6000 }
        );
      }

      // Invalidate queries to ensure data consistency
      queryClient.invalidateQueries(["invoices", clientId]);
      queryClient.invalidateQueries(["invoiceStats", clientId]);
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousInvoices) {
        queryClient.setQueryData(
          ["invoices", clientId, adminPage, adminLimit, status],
          context.previousInvoices
        );
      }
      toast.error(err.message || "Failed to update invoice");
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries(["invoices", clientId]);
    },
  });

  // Delete invoice mutation
  const deleteInvoiceMutation = useMutation(deleteInvoice, {
    onMutate: async (invoiceId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(["invoices", clientId]);

      // Snapshot the previous value
      const previousInvoices = queryClient.getQueryData([
        "invoices",
        clientId,
        adminPage,
        adminLimit,
        status,
      ]);

      // Optimistically update to remove the deleted invoice
      queryClient.setQueryData(
        ["invoices", clientId, adminPage, adminLimit, status],
        (old) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.filter((invoice) => invoice.id !== invoiceId),
            total: Math.max(0, (old.total || 0) - 1),
          };
        }
      );

      return { previousInvoices };
    },
    onSuccess: (_, invoiceId) => {
      toast.success("Invoice deleted successfully");

      // Invalidate queries to ensure data consistency
      queryClient.invalidateQueries(["invoices", clientId]);
      queryClient.invalidateQueries(["invoiceStats", clientId]);
    },
    onError: (err, invoiceId, context) => {
      // Rollback on error
      if (context?.previousInvoices) {
        queryClient.setQueryData(
          ["invoices", clientId, adminPage, adminLimit, status],
          context.previousInvoices
        );
      }
      toast.error(err.message || "Failed to delete invoice");
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries(["invoices", clientId]);
    },
  });

  // Mark invoice as paid mutation
  const markAsPaidMutation = useMutation(markInvoiceAsPaid, {
    onMutate: async ({ invoiceId, paymentData }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(["invoices", clientId]);

      // Snapshot the previous value
      const previousInvoices = queryClient.getQueryData([
        "invoices",
        clientId,
        adminPage,
        adminLimit,
        status,
      ]);

      // Optimistically update to mark the invoice as paid
      queryClient.setQueryData(
        ["invoices", clientId, adminPage, adminLimit, status],
        (old) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.map((invoice) =>
              invoice._id === invoiceId
                ? {
                    ...invoice,
                    status: "paid",
                    paid_at: paymentData.payment_date,
                    payment_method: paymentData.payment_method,
                    payment_reference: paymentData.payment_reference,
                  }
                : invoice
            ),
          };
        }
      );

      return { previousInvoices };
    },
    onSuccess: (updatedInvoice) => {
      toast.success("Invoice marked as paid successfully");

      // Invalidate queries to ensure data consistency
      queryClient.invalidateQueries(["invoices", clientId]);
      queryClient.invalidateQueries(["invoiceStats", clientId]);
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousInvoices) {
        queryClient.setQueryData(
          ["invoices", clientId, adminPage, adminLimit, status],
          context.previousInvoices
        );
      }
      toast.error(err.message || "Failed to mark invoice as paid");
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries(["invoices", clientId]);
    },
  });

  // Cancel invoice mutation
  const cancelInvoiceMutation = useMutation(cancelInvoice, {
    onMutate: async ({ invoiceId, reason }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(["invoices", clientId]);

      // Snapshot the previous value
      const previousInvoices = queryClient.getQueryData([
        "invoices",
        clientId,
        adminPage,
        adminLimit,
        status,
      ]);

      // Get the current date in ISO format
      const currentDate = new Date().toISOString();

      // Optimistically update to mark the invoice as canceled
      queryClient.setQueryData(
        ["invoices", clientId, adminPage, adminLimit, status],
        (old) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.map((invoice) =>
              invoice._id === invoiceId
                ? {
                    ...invoice,
                    status: "canceled",
                    cancelled_at: currentDate,
                    cancellation_reason: reason,
                  }
                : invoice
            ),
          };
        }
      );

      return { previousInvoices };
    },
    onSuccess: (updatedInvoice) => {
      toast.success("Invoice canceled successfully");

      // Invalidate queries to ensure data consistency
      queryClient.invalidateQueries(["invoices", clientId]);
      queryClient.invalidateQueries(["invoiceStats", clientId]);
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousInvoices) {
        queryClient.setQueryData(
          ["invoices", clientId, adminPage, adminLimit, status],
          context.previousInvoices
        );
      }
      toast.error(err.message || "Failed to cancel invoice");
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries(["invoices", clientId]);
    },
  });
  
  // Restore invoice mutation
  const restoreInvoiceMutation = useMutation(restoreInvoice, {
    onMutate: async ({ invoiceId, notes }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(["invoices", clientId]);

      // Snapshot the previous value
      const previousInvoices = queryClient.getQueryData([
        "invoices",
        clientId,
        adminPage,
        adminLimit,
        status,
      ]);
      
      // Get the current date in ISO format
      const currentDate = new Date().toISOString();

      // Optimistically update to restore the invoice to draft status
      queryClient.setQueryData(
        ["invoices", clientId, adminPage, adminLimit, status],
        (old) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.map((invoice) =>
              invoice._id === invoiceId
                ? {
                    ...invoice,
                    status: "draft",
                    restored_at: currentDate,
                    restoration_notes: notes,
                    // Keep cancellation info for reference
                    cancelled_at: invoice.cancelled_at,
                    cancellation_reason: invoice.cancellation_reason
                  }
                : invoice
            ),
          };
        }
      );

      return { previousInvoices };
    },
    onSuccess: (updatedInvoice) => {
      toast.success("Invoice restored successfully");
      
      // Invalidate queries to ensure data consistency
      queryClient.invalidateQueries(["invoices", clientId]);
      queryClient.invalidateQueries(["invoiceStats", clientId]);
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousInvoices) {
        queryClient.setQueryData(
          ["invoices", clientId, adminPage, adminLimit, status],
          context.previousInvoices
        );
      }
      
      // Special handling for detailed error messages, especially for Stripe invoices
      // that cannot be restored
      toast.error(err.message || "Failed to restore invoice");
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries(["invoices", clientId]);
    },
  });

  //   // Create invoice mutation
  //   const createInvoice = useMutation(
  //     async (invoiceData) => {
  //       const response = await apiAxios.post('/invoices', invoiceData);
  //       return response.data;
  //     },
  //     {
  //       onSuccess: (data) => {
  //         queryClient.invalidateQueries(['invoices', clientId]);
  //         toast.success('Invoice created successfully');
  //       },
  //       onError: (error) => {
  //         toast.error(error.response?.data?.detail || 'Failed to create invoice');
  //       }
  //     }
  //   );

  // Create client payment intent mutation
  const createPaymentIntentMutation = useMutation(createClientPaymentIntent, {
    onError: (error) => {
      toast.error(error.message || "Failed to create payment intent");
    },
  });

  // Send invoice email mutation
  const sendInvoiceEmailMutation = useMutation(sendInvoiceEmail, {
    onSuccess: () => {
      toast.success("Invoice email sent successfully");
      queryClient.invalidateQueries(["invoices", clientId]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to send invoice email");
    },
  });

  return {
    invoices: data?.items || [],
    pagination: {
      currentPage: data?.page || 0,
      totalPages: data?.total_pages || 0,
      totalItems: data?.total || 0,
      hasMore: data?.has_more || false,
    },
    isLoading,
    isError,
    error,
    refetch,

    // Client Portal Invoices
    clientPortalInvoices,
    clientPortalPagination: {
      currentPage: clientPortalData?.page || 0,
      totalPages: clientPortalData?.total_pages || 0,
      totalItems: clientPortalData?.total || 0,
      hasMore: clientPortalData?.has_more || false,
    },
    isLoadingClientPortal,
    isErrorClientPortal,
    errorClientPortal,
    refetchClientPortal,

    // Mutations
    createInvoice: createInvoiceMutation.mutateAsync,
    updateInvoice: updateInvoiceMutation.mutateAsync,
    deleteInvoice: deleteInvoiceMutation.mutateAsync,
    markInvoiceAsPaid: markAsPaidMutation.mutateAsync,
    cancelInvoice: cancelInvoiceMutation.mutateAsync,
    restoreInvoice: restoreInvoiceMutation.mutateAsync,
    createPaymentIntent: createPaymentIntentMutation.mutateAsync,
    sendInvoiceEmail: sendInvoiceEmailMutation.mutateAsync,

    // Loading States
    isCreating: createInvoiceMutation.isLoading,
    isUpdating: updateInvoiceMutation.isLoading,
    isDeleting: deleteInvoiceMutation.isLoading,
    isMarkingAsPaid: markAsPaidMutation.isLoading,
    isCancelling: cancelInvoiceMutation.isLoading,
    isRestoring: restoreInvoiceMutation.isLoading,
    isCreatingPaymentIntent: createPaymentIntentMutation.isLoading,
    isSendingEmail: sendInvoiceEmailMutation.isLoading,

    // Error States
    createError: createInvoiceMutation.error,
    updateError: updateInvoiceMutation.error,
    deleteError: deleteInvoiceMutation.error,
    markAsPaidError: markAsPaidMutation.error,
    cancelError: cancelInvoiceMutation.error,
    restoreError: restoreInvoiceMutation.error,
  };
};

export function useClientInvoices({ page = 0, limit = 20 } = {}) {
  const queryClient = useQueryClient();

  // Build the query key to track caching / data
  const queryKey = ["clientPortalInvoices", page, limit];

  // Execute React Query to fetch client portal invoices
  const { data, isLoading, isError, error, refetch } = useQuery(
    queryKey,
    () => getClientInvoices({ page, limit }),
    {
      keepPreviousData: true,
      // Portal invoice status changes out-of-band (payment -> webhook -> DB), so
      // keep it fresh without a manual reload: refetch on mount/focus and poll
      // while the page is open (only when focused).
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchInterval: 30 * 1000, // 30s live poll
    }
  );

  // Create client payment intent mutation
  const createPaymentIntentMutation = useMutation(createClientPaymentIntent, {
    onError: (error) => {
      toast.error(error.message || "Failed to create payment intent");
    },
  });

  // Extract the items array and pagination info from the data, if available
  const items = data?.items || [];
  const pagination = {
    currentPage: data?.page || 0,
    totalPages: data?.total_pages || 0,
    totalItems: data?.total || 0,
    hasMore: data?.has_more || false,
  };

  // Return the data and helper states
  return {
    clientInvoices: items,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    createPaymentIntent: createPaymentIntentMutation.mutateAsync,
    isCreatingPaymentIntent: createPaymentIntentMutation.isLoading,
    invalidateInvoices: () => queryClient.invalidateQueries(["clientPortalInvoices"]),
  };
}
